import * as React from 'react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'app/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from 'app/components/ui/card';
import { useToast } from 'app/components/ui/use-toast';
import { useMediaSlice } from '../Media/slice';
import { mediaBaseUrl } from 'utils/api/endpoints';
import {
    OfflinePaymentMethod,
    OfflinePaymentMethodFormData,
    validationRules,
} from '../../types/offlinePayment';
import {
    Smartphone,
    BanknoteIcon,
    QrCode,
    User,
    Info,
    Image as ImageIcon,
} from 'lucide-react';

interface OfflinePaymentFormProps {
    mode: 'view' | 'edit' | 'add';
    method: OfflinePaymentMethod | null;
    category: 'indian' | 'worldwide';
    methodType: string;
    onSubmit: (data: OfflinePaymentMethodFormData) => void;
    onCancel?: () => void;
    isLoading: boolean;
}

const iconOptions = [
    { value: 'Smartphone', label: 'Smartphone', icon: Smartphone },
    { value: 'BanknoteIcon', label: 'Banknote', icon: BanknoteIcon },
    { value: 'QrCode', label: 'QR Code', icon: QrCode },
    { value: 'User', label: 'User', icon: User },
    { value: 'Info', label: 'Info', icon: Info },
];

const methodTypeOptions = {
    indian: [
        { value: 'upi', label: 'UPI' },
        { value: 'paytm', label: 'Paytm' },
        { value: 'phonepe', label: 'PhonePe' },
        { value: 'googlepay', label: 'Google Pay' },
    ],
    worldwide: [
        { value: 'crypto', label: 'Crypto' },
        { value: 'paypal', label: 'PayPal' },
        { value: 'binance', label: 'Binance' },
    ],
};

export default function OfflinePaymentForm({
    mode,
    method,
    category,
    methodType,
    onSubmit,
    onCancel,
    isLoading,
}: OfflinePaymentFormProps) {
    const { toast } = useToast();
    const { useUploadMediaMutation } = useMediaSlice();
    const [uploadFile, { isLoading: isUploadingImage }] =
        useUploadMediaMutation();

    const [formData, setFormData] = React.useState<OfflinePaymentMethodFormData>({
        category: category,
        methodType: methodType || '',
        title: method?.title || '',
        icon: method?.icon || 'Smartphone',
        badge: method?.badge || '',
        details: method?.details || {},
    });

    const [errors, setErrors] = React.useState<Record<string, string>>({});

    // Update form data when props change
    React.useEffect(() => {
        if (method) {
            setFormData({
                category: category,
                methodType: methodType,
                title: method.title,
                icon: method.icon,
                badge: method.badge,
                details: method.details,
            });
        } else if (mode === 'add') {
            setFormData({
                category: category,
                methodType: '',
                title: '',
                icon: 'Smartphone',
                badge: '',
                details: {},
            });
        }
    }, [method, category, methodType, mode]);

    // Validation function
    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        const rules = validationRules[category];

        // Validate required fields
        rules.required.forEach(field => {
            if (
                field === 'upiId' &&
                category === 'indian' &&
                !formData.details.upiId?.trim()
            ) {
                newErrors.upiId = (rules.validation as any).upiId.message;
            }
            if (
                field === 'recipientName' &&
                category === 'indian' &&
                !formData.details.recipientName?.trim()
            ) {
                newErrors.recipientName = (
                    rules.validation as any
                ).recipientName.message;
            }
            if (
                field === 'walletAddress' &&
                category === 'worldwide' &&
                !formData.details.walletAddress?.trim()
            ) {
                newErrors.walletAddress = (
                    rules.validation as any
                ).walletAddress.message;
            }
            if (field === 'qrCodeImage' && !formData.details.qrCodeImage?.trim()) {
                newErrors.qrCodeImage = rules.validation.qrCodeImage.message;
            }
        });

        // Validate patterns and lengths
        if (category === 'indian') {
            // Only validate UPI ID if it's provided (since it's optional)
            if (formData.details.upiId && (rules.validation as any).upiId.pattern) {
                const pattern = new RegExp((rules.validation as any).upiId.pattern);
                if (!pattern.test(formData.details.upiId)) {
                    newErrors.upiId = (rules.validation as any).upiId.message;
                }
            }

            // Only validate recipient name if it's provided (since it's optional)
            if (formData.details.recipientName) {
                const { minLength, maxLength } = (rules.validation as any)
                    .recipientName;
                if (minLength && formData.details.recipientName.length < minLength) {
                    newErrors.recipientName = (
                        rules.validation as any
                    ).recipientName.message;
                }
                if (maxLength && formData.details.recipientName.length > maxLength) {
                    newErrors.recipientName = (
                        rules.validation as any
                    ).recipientName.message;
                }
            }
        }

        if (category === 'worldwide') {
            // Only validate wallet address if it's provided (since it's optional)
            if (formData.details.walletAddress) {
                const { minLength, maxLength } = (rules.validation as any)
                    .walletAddress;
                if (minLength && formData.details.walletAddress.length < minLength) {
                    newErrors.walletAddress = (
                        rules.validation as any
                    ).walletAddress.message;
                }
                if (maxLength && formData.details.walletAddress.length > maxLength) {
                    newErrors.walletAddress = (
                        rules.validation as any
                    ).walletAddress.message;
                }
            }

            // Only validate recipient name if it's provided (since it's optional)
            if (formData.details.recipientName) {
                const { minLength, maxLength } = (rules.validation as any)
                    .recipientName;
                if (minLength && formData.details.recipientName.length < minLength) {
                    newErrors.recipientName = (
                        rules.validation as any
                    ).recipientName.message;
                }
                if (maxLength && formData.details.recipientName.length > maxLength) {
                    newErrors.recipientName = (
                        rules.validation as any
                    ).recipientName.message;
                }
            }
        }

        // Validate required form fields for add and edit modes
        if (mode === 'add' || mode === 'edit') {
            if (!formData.title.trim()) {
                newErrors.title = 'Title is required';
            }
            if (!formData.methodType.trim()) {
                newErrors.methodType = 'Method type is required';
            }
            if (!formData.badge.trim()) {
                newErrors.badge = 'Badge is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast({
                title: 'Validation Error',
                description: 'Please fix the errors before submitting',
                variant: 'destructive',
            });
            return;
        }

        onSubmit(formData);
    };

    // Handle input changes
    const handleInputChange = (field: string, value: string) => {
        if (field.startsWith('details.')) {
            const detailField = field.replace('details.', '');
            setFormData(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    [detailField]: value,
                },
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value,
            }));
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    // Handle file upload
    const handleFileUpload = async (file: File) => {
        const formData = new FormData();
        formData.append('payment-gateway', file);

        try {
            const response = await uploadFile(formData).unwrap();

            if (response?.response?.['payment-gateway']?.[0]) {
                const uploadedFileName = response.response['payment-gateway'][0];
                handleInputChange('details.qrCodeImage', `${uploadedFileName}`);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            toast({
                title: 'Failed to upload image',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    const isReadOnly = mode === 'view';

    return (
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
            {/* Basic Information - For add and edit modes */}
            {(mode === 'add' || mode === 'edit') && (
                <Card>
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                        <CardDescription>
                            Configure the basic details of the payment method
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="title" className="mb-2">
                                    Title <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={e => handleInputChange('title', e.target.value)}
                                    placeholder="e.g., Indian Users"
                                    disabled={isReadOnly}
                                />
                                {errors.title && (
                                    <p className="text-sm text-red-600 mt-1">{errors.title}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="badge" className="mb-2">
                                    Badge <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="badge"
                                    value={formData.badge}
                                    onChange={e => handleInputChange('badge', e.target.value)}
                                    placeholder="e.g., UPI"
                                    disabled={isReadOnly}
                                />
                                {errors.badge && (
                                    <p className="text-sm text-red-600 mt-1">{errors.badge}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="methodType" className="mb-2">
                                    Method Type <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                    value={formData.methodType}
                                    onValueChange={value =>
                                        handleInputChange('methodType', value)
                                    }
                                    disabled={isReadOnly}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select method type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {methodTypeOptions[category].map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.methodType && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {errors.methodType}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="icon" className="mb-2">
                                    Icon
                                </Label>
                                <Select
                                    value={formData.icon}
                                    onValueChange={value => handleInputChange('icon', value)}
                                    disabled={isReadOnly}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select icon" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {iconOptions.map(option => {
                                            const IconComponent = option.icon;
                                            return (
                                                <SelectItem key={option.value} value={option.value}>
                                                    <div className="flex items-center gap-2">
                                                        <IconComponent className="w-4 h-4" />
                                                        {option.label}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Payment Details */}
            <Card>
                <CardHeader>
                    <CardTitle>Payment Details</CardTitle>
                    <CardDescription>
                        Configure the specific payment details for {category} users
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Indian Payment Methods */}
                    {category === 'indian' && (
                        <>
                            <div>
                                <Label htmlFor="upiId" className="mb-2">
                                    UPI ID{' '}
                                    <span className="text-gray-500 text-xs">(Optional)</span>
                                </Label>
                                <Input
                                    id="upiId"
                                    value={formData.details.upiId || ''}
                                    onChange={e =>
                                        handleInputChange('details.upiId', e.target.value)
                                    }
                                    placeholder="e.g., user@bank"
                                    disabled={isReadOnly}
                                />
                                {errors.upiId && (
                                    <p className="text-sm text-red-600 mt-1">{errors.upiId}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="recipientName" className="mb-2">
                                    Recipient Name{' '}
                                    <span className="text-gray-500 text-xs">(Optional)</span>
                                </Label>
                                <Input
                                    id="recipientName"
                                    value={formData.details.recipientName || ''}
                                    onChange={e =>
                                        handleInputChange('details.recipientName', e.target.value)
                                    }
                                    placeholder="e.g., John Doe"
                                    disabled={isReadOnly}
                                />
                                {errors.recipientName && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {errors.recipientName}
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {/* Worldwide Payment Methods */}
                    {category === 'worldwide' && (
                        <>
                            <div>
                                <Label htmlFor="walletAddress" className="mb-2">
                                    {formData.methodType === 'crypto' && 'Crypto Wallet Address'}
                                    {formData.methodType === 'paypal' && 'PayPal Email/ID'}
                                    {formData.methodType === 'binance' && 'Binance Pay ID'}
                                    {(!formData.methodType || formData.methodType === '') &&
                                        'Payment Address'}{' '}
                                    <span className="text-gray-500 text-xs">(Optional)</span>
                                </Label>
                                <Input
                                    id="walletAddress"
                                    key={`walletAddress-${formData.methodType}`}
                                    value={formData.details.walletAddress || ''}
                                    onChange={e =>
                                        handleInputChange('details.walletAddress', e.target.value)
                                    }
                                    placeholder={
                                        formData.methodType === 'crypto'
                                            ? 'e.g., 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'
                                            : formData.methodType === 'paypal'
                                                ? 'e.g., merchant@paypal.com'
                                                : formData.methodType === 'binance'
                                                    ? 'e.g., 1234567890'
                                                    : 'e.g., payment address or ID'
                                    }
                                    disabled={isReadOnly}
                                />
                                {errors.walletAddress && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {errors.walletAddress}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="recipientName" className="mb-2">
                                    Recipient Name{' '}
                                    <span className="text-gray-500 text-xs">(Optional)</span>
                                </Label>
                                <Input
                                    id="recipientName"
                                    value={formData.details.recipientName || ''}
                                    onChange={e =>
                                        handleInputChange('details.recipientName', e.target.value)
                                    }
                                    placeholder="e.g., John Smith"
                                    disabled={isReadOnly}
                                />
                                {errors.recipientName && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {errors.recipientName}
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {/* QR Code Image */}
                    <div>
                        <Label htmlFor="qrCodeImage" className="mb-2">
                            QR Code Image <span className="text-red-500">*</span>
                        </Label>
                        <div className="space-y-3">
                            {/* Current Image Preview */}
                            {formData.details.qrCodeImage && (
                                <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                                    <div className="flex-shrink-0">
                                        <img
                                            src={`${mediaBaseUrl}/payment-gateway/${formData.details.qrCodeImage}`}
                                            alt="QR Code"
                                            className="w-16 h-16 object-contain rounded"
                                            onError={e => {
                                                e.currentTarget.style.display = 'none';
                                                const nextElement = e.currentTarget
                                                    .nextElementSibling as HTMLElement;
                                                if (nextElement) {
                                                    nextElement.style.display = 'block';
                                                }
                                            }}
                                        />
                                        <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center hidden">
                                            <ImageIcon className="w-8 h-8 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-700 mb-1">
                                            {isUploadingImage ? 'Uploading...' : 'Current QR Code'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formData.details.qrCodeImage}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* File Upload */}
                            {!isReadOnly && (
                                <div>
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg"
                                        className="w-full border rounded px-3 py-2 text-sm"
                                        disabled={isUploadingImage}
                                        onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                handleFileUpload(file);
                                            }
                                        }}
                                    />
                                    {isUploadingImage && (
                                        <p className="text-xs text-blue-600 mt-1">
                                            Uploading image...
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        {errors.qrCodeImage && (
                            <p className="text-sm text-red-600 mt-1">{errors.qrCodeImage}</p>
                        )}
                    </div>

                    {/* Additional Info */}
                    <div>
                        <Label htmlFor="additionalInfo" className="mb-2">
                            Additional Information
                        </Label>
                        <Textarea
                            id="additionalInfo"
                            value={formData.details.additionalInfo || ''}
                            onChange={e =>
                                handleInputChange('details.additionalInfo', e.target.value)
                            }
                            placeholder={
                                category === 'indian'
                                    ? 'e.g., Scan QR code or use UPI ID for payment'
                                    : formData.methodType === 'crypto'
                                        ? 'e.g., Send crypto to the wallet address above'
                                        : formData.methodType === 'paypal'
                                            ? 'e.g., Send money via PayPal to the email above'
                                            : formData.methodType === 'binance'
                                                ? 'e.g., Pay via Binance Pay using the ID above'
                                                : 'e.g., Payment instructions for users'
                            }
                            rows={3}
                            disabled={isReadOnly}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Form Actions */}
            {!isReadOnly && (
                <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading
                            ? 'Saving...'
                            : mode === 'add'
                                ? 'Add Payment Method'
                                : 'Update Payment Method'}
                    </Button>
                </div>
            )}
        </form>
    );
}
